import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { optionLabel, optionValue, uiLang } from '../kinds';
import api from '../config/api';
import { fieldKey } from '../fieldKeys';
import { MAX_IMAGE_BYTES } from '../fieldTypes';
import LocationPicker from './LocationPicker';
import { pathLabel, useLocations } from '../locations';
import { errorMessage } from '../errors';
import { Alert, Button, Card, Dialog, EmptyState, IconButton } from '../ui';
import { CameraIcon, ChevronLeftIcon, ImageIcon, TrashIcon } from '../ui/icons';

function ImageImportModal({ inventory, onClose, onImport }) {
  const { t, i18n } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedItems, setExtractedItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  // The photo as the server kept it (a media of the collection); items created from it name it.
  const [sourceMediaId, setSourceMediaId] = useState(null);
  // Photos are not the model's to fill: image fields are left out of the review.
  const fields = (inventory.fields || []).filter(field => field.type !== 'image');
  // The model's place is matched by the server (location_id, location_path); it can be changed here.
  const hasLocations = !!inventory.location_type_id;
  const { places } = useLocations(inventory.location_type_id);
  const fileRef = useRef(null);
  // Each step is its own dialog (a key apiece): the review starts on its first item's heading.
  const firstItemRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError(t('imageImport.notImage'));
        return;
      }

      // Validate file size (as the server: 15 MB)
      if (file.size > MAX_IMAGE_BYTES) {
        setError(t('imageImport.tooBig'));
        return;
      }

      setSelectedFile(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExtractItems = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('inventory_id', inventory.id);

      const response = await api.post('/extract-items', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // `matched`: the place came from the model's answer (shown until changed by hand).
      setExtractedItems((response.data.items || []).map(item => ({ ...item, matched: item.location_id != null })));
      setSourceMediaId(response.data.source_media_id || null);
      setIsValidating(true);
    } catch (error) {
      console.error('Error extracting items:', error);
      const code = error.response?.data?.code;
      setError(['invalid_image', 'image_too_large', 'busy'].includes(code)
        ? t(`mediaErrors.${code}`)
        : errorMessage(t, error, 'imageImport.extractError'));
    } finally {
      setIsProcessing(false);
    }
  };

  // The server returns values by field key (the AI's field names are mapped there).
  const handleFieldChange = (itemIndex, key, value) => {
    setExtractedItems(items => 
      items.map((item, index) => 
        index === itemIndex 
          ? { ...item, field_values: { ...item.field_values, [key]: value } }
          : item
      )
    );
  };

  const handleLocationChange = (itemIndex, locationId) => {
    const place = places.find(p => p.id === locationId);
    setExtractedItems(items => items.map((item, index) => (index === itemIndex
      ? { ...item, location_id: locationId, location_path: place ? place.path : [], matched: false }
      : item)));
  };

  const handleRemoveItem = (itemIndex) => {
    setExtractedItems(items => items.filter((_, index) => index !== itemIndex));
  };

  const handleImportItems = () => {
    // What the server stores: values, and the place when the collection has places.
    onImport(extractedItems.map(item => (hasLocations
      ? { field_values: item.field_values, location_id: item.location_id ?? null }
      : { field_values: item.field_values })), sourceMediaId);
  };

  const renderFieldInput = (item, itemIndex, field, id) => {
    const key = fieldKey(field);
    const value = item.field_values[key] || '';

    switch (field.type) {
      case 'select':
        return (
          <select id={id} value={value} onChange={(e) => handleFieldChange(itemIndex, key, e.target.value)}>
            <option value="">{t('imageImport.select')}</option>
            {field.options && field.options.map((option, index) => (
              <option key={index} value={optionValue(option)}>{optionLabel(option, uiLang(i18n))}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="item-choices">
            {field.options && field.options.map((option, index) => (
              <label key={index} className="item-choice">
                <input
                  type="radio"
                  name={`${itemIndex}_${key}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleFieldChange(itemIndex, key, e.target.value)}
                />
                {option}
              </label>
            ))}
          </div>
        );
      case 'checkbox': {
        const checkboxValues = Array.isArray(value) ? value : [];
        return (
          <div className="item-choices">
            {field.options && field.options.map((option, index) => (
              <label key={index} className="item-choice">
                <input
                  type="checkbox"
                  value={option}
                  checked={checkboxValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...checkboxValues, option]
                      : checkboxValues.filter(v => v !== option);
                    handleFieldChange(itemIndex, key, newValues);
                  }}
                />
                {option}
              </label>
            ))}
          </div>
        );
      }
      case 'textarea':
        return <textarea id={id} value={value} rows="2" onChange={(e) => handleFieldChange(itemIndex, key, e.target.value)} />;
      default:
        return <input id={id} type={field.type || 'text'} value={value} onChange={(e) => handleFieldChange(itemIndex, key, e.target.value)} />;
    }
  };

  const grouped = (type) => ['radio', 'checkbox'].includes(type);

  if (!isValidating) {
    return (
      <Dialog
        key="pick"
        open
        onClose={onClose}
        title={t('imageImport.title')}
        description={t('imageImport.help')}
        size="lg"
        footer={(
          <>
            <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
            <Button onClick={handleExtractItems} disabled={!selectedFile} loading={isProcessing}>
              {isProcessing ? t('imageImport.processing') : t('imageImport.extract')}
            </Button>
          </>
        )}
      >
        <div className="dialog-form">
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFileSelect} hidden />
          {imagePreview ? (
            <figure className="photo-import__preview">
              <img src={imagePreview} alt={t('imageImport.preview')} />
              <figcaption>
                <span className="photo-import__name">{selectedFile?.name}</span>
                <Button variant="secondary" size="sm" icon={CameraIcon} onClick={() => fileRef.current?.click()}>{t('imageImport.another')}</Button>
              </figcaption>
            </figure>
          ) : (
            <button type="button" className="file-drop" onClick={() => fileRef.current?.click()}>
              <span className="file-drop__icon"><CameraIcon size={22} /></span>
              <span className="file-drop__title">{t('imageImport.selectImage')}</span>
              <span className="file-drop__hint">{t('imageImport.choose')}</span>
            </button>
          )}
          {error && <Alert tone="danger">{error}</Alert>}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      key="review"
      open
      onClose={onClose}
      initialFocusRef={extractedItems.length > 0 ? firstItemRef : undefined}
      title={t('imageImport.extracted', { count: extractedItems.length })}
      description={extractedItems.length > 0 ? t('imageImport.review') : undefined}
      size="xl"
      footer={(
        <>
          <Button variant="ghost" icon={ChevronLeftIcon} onClick={() => setIsValidating(false)} className="dialog-footer__back">
            {t('imageImport.back')}
          </Button>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleImportItems} disabled={extractedItems.length === 0}>
            {t('imageImport.import', { count: extractedItems.length })}
          </Button>
        </>
      )}
    >
      {extractedItems.length === 0 ? (
        <EmptyState icon={ImageIcon} title={t('imageImport.none')}
                    action={<Button variant="secondary" onClick={() => setIsValidating(false)}>{t('imageImport.tryAnother')}</Button>} />
      ) : (
        <ol className="photo-import__items">
          {extractedItems.map((item, itemIndex) => {
            const headingId = `import-item-${itemIndex}`;
            return (
              <li key={itemIndex}>
                <Card as="section" aria-labelledby={headingId} className="photo-import__item">
                  <div className="photo-import__head">
                    <h3 id={headingId} className="settings-card__title" tabIndex={-1} ref={itemIndex === 0 ? firstItemRef : undefined}>{t('imageImport.item', { number: itemIndex + 1 })}</h3>
                    <IconButton icon={TrashIcon} variant="ghost" className="field-row__remove"
                                label={t('imageImport.removeItem', { number: itemIndex + 1 })} onClick={() => handleRemoveItem(itemIndex)} />
                  </div>
                  <div className="photo-import__fields">
                    {fields.map((field, fieldIndex) => {
                      const id = `import-${itemIndex}-${fieldIndex}`;
                      const label = (
                        <>
                          {field.name}
                          {field.required && <span className="item-field__required" aria-hidden="true"> *</span>}
                        </>
                      );
                      return (
                        <div key={fieldIndex} className="item-field">
                          {grouped(field.type) ? (
                            <div role="group" aria-labelledby={`${id}-label`}>
                              <span id={`${id}-label`} className="ui-field__label">{label}</span>
                              {renderFieldInput(item, itemIndex, field, id)}
                            </div>
                          ) : (
                            <>
                              <label htmlFor={id} className="ui-field__label">{label}</label>
                              {renderFieldInput(item, itemIndex, field, id)}
                            </>
                          )}
                        </div>
                      );
                    })}

                    {hasLocations && (
                      <div className="item-field">
                        <label htmlFor={`import-location-${itemIndex}`} className="ui-field__label">{t('collection.location')}</label>
                        <LocationPicker
                          inputId={`import-location-${itemIndex}`}
                          places={places}
                          value={item.location_id ?? null}
                          onChange={(id) => handleLocationChange(itemIndex, id)}
                        />
                        {item.matched && item.location_path && item.location_path.length > 0 && (
                          <p className="ui-field__hint photo-import__matched">{t('imageImport.matchedPlace', { path: pathLabel(t, item.location_path) })}</p>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ol>
      )}
    </Dialog>
  );
}

export default ImageImportModal;